/**
 * Sales Summary dashboard (Tabs-updates batch). Replaces the old
 * `/sales` queue content with a roll-up of leads, lead actions,
 * opportunities, opportunity actions, plus a reserved (blank) tasks panel.
 *
 * Reference: docs/plans/leads-opps-tabs-updates.md section 2.7.
 */

import {
  INDUSTRY,
  INDUSTRY_LABELS,
  LEAD_ACTION,
  LEAD_ACTION_COLORS,
  LEAD_ACTION_LABELS,
  LEAD_SOURCE,
  LEAD_STAGE,
  NEXT_LEAD_ACTION,
  NEXT_OPPORTUNITY_ACTION,
  OPPORTUNITY_ACTION,
  OPPORTUNITY_ACTION_COLORS,
  OPPORTUNITY_ACTION_LABELS,
  OPPORTUNITY_STAGE,
  CUSTOMER_STATUS,
  type LeadAction,
  type OpportunityAction,
} from '@workspace/shared';

import { useSalesSummary } from '@/lib/api/sales';

function formatCurrency(num: number): string {
  if (num >= 1_000_000) return `$${(num / 1_000_000).toFixed(2)}M`;
  if (num >= 1_000) return `$${(num / 1_000).toFixed(0)}K`;
  return `$${num.toFixed(0)}`;
}

function Panel({
  eyebrowNum,
  label,
  children,
}: {
  eyebrowNum: string;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <section className="border border-mx-grey-800 bg-mx-card p-mx-4">
      <p className="mono-label text-mx-grey-mid">
        <span className="num">{eyebrowNum}</span>
        {label}
      </p>
      <div className="mt-mx-3">{children}</div>
    </section>
  );
}

/** Stat tile (big number + caption). */
function Stat({ value, caption }: { value: string; caption: string }) {
  return (
    <div className="flex flex-col gap-mx-1 border border-mx-grey-800 bg-mx-grey-900 px-mx-3 py-mx-2">
      <p className="tnum font-display text-mx-h2 font-semibold text-mx-grey-300">{value}</p>
      <p className="mono-label text-mx-grey-mid">{caption}</p>
    </div>
  );
}

/** A label → count breakdown rendered as a bar list. */
function Breakdown({
  rows,
}: {
  rows: { key: string; label: string; count: number; color?: string }[];
}) {
  const max = Math.max(1, ...rows.map((r) => r.count));
  const visible = rows.filter((r) => r.count > 0);
  if (visible.length === 0) {
    return <p className="text-mx-body-sm text-mx-grey-mid">No records.</p>;
  }
  return (
    <ul className="flex flex-col gap-mx-1">
      {visible.map((r) => (
        <li key={r.key} className="flex items-center gap-mx-2">
          <span className="w-[160px] shrink-0 truncate text-mx-body-sm text-mx-grey-300">
            {r.color ? (
              <span
                className="mono-label mr-mx-2 px-mx-1 py-[1px] text-mx-mono-sm"
                style={{ backgroundColor: r.color, color: '#fff' }}
              >
                {r.label}
              </span>
            ) : (
              r.label
            )}
          </span>
          <span className="h-[6px] flex-1 bg-mx-grey-900">
            <span
              className="block h-full bg-mx-orange"
              style={{ width: `${(r.count / max) * 100}%` }}
            />
          </span>
          <span className="tnum w-8 shrink-0 text-right text-mx-body-sm text-mx-grey-300">
            {r.count}
          </span>
        </li>
      ))}
    </ul>
  );
}

export function SummaryDashboard() {
  const { data, isLoading, isError } = useSalesSummary();

  if (isLoading) return <p className="mono-label text-mx-grey-mid">LOADING…</p>;
  if (isError || !data)
    return <p className="text-mx-body-sm text-mx-neg">Could not load the sales summary.</p>;

  const { leads, opportunities: opps } = data;

  return (
    <div className="flex flex-col gap-mx-4 pb-mx-6">
      {/* 1 — Leads summary */}
      <Panel eyebrowNum="01" label="Leads">
        <div className="grid grid-cols-2 gap-mx-2 sm:grid-cols-4">
          <Stat value={leads.total.toString()} caption="TOTAL LEADS" />
          <Stat value={(leads.byStage.TARGET ?? 0).toString()} caption="TARGET" />
          <Stat value={(leads.byStage.ACTIVE ?? 0).toString()} caption="ACTIVE" />
          <Stat value={formatCurrency(leads.totalPotentialValue)} caption="POTENTIAL VALUE" />
        </div>
        <div className="mt-mx-4 grid grid-cols-1 gap-mx-4 lg:grid-cols-3">
          <div>
            <p className="mono-label mb-mx-2 text-mx-grey-mid">BY STAGE</p>
            <Breakdown
              rows={LEAD_STAGE.map((s) => ({ key: s, label: s, count: leads.byStage[s] ?? 0 }))}
            />
          </div>
          <div>
            <p className="mono-label mb-mx-2 text-mx-grey-mid">BY SOURCE</p>
            <Breakdown
              rows={LEAD_SOURCE.map((s) => ({
                key: s,
                label: s.replace(/_/g, ' '),
                count: leads.bySource[s] ?? 0,
              }))}
            />
          </div>
          <div>
            <p className="mono-label mb-mx-2 text-mx-grey-mid">BY INDUSTRY</p>
            <Breakdown
              rows={INDUSTRY.map((i) => ({
                key: i,
                label: INDUSTRY_LABELS[i],
                count: leads.byIndustry[i] ?? 0,
              }))}
            />
          </div>
        </div>
      </Panel>

      {/* 2 — Lead current + next actions */}
      <Panel eyebrowNum="02" label="Lead actions">
        <Breakdown
          rows={LEAD_ACTION.map((a) => {
            const next = NEXT_LEAD_ACTION[a as LeadAction];
            const nextLabel = next ? ` → ${LEAD_ACTION_LABELS[next]}` : '';
            return {
              key: a,
              label: `${LEAD_ACTION_LABELS[a]}${nextLabel}`,
              count: leads.byCurrentAction[a] ?? 0,
              color: LEAD_ACTION_COLORS[a].bg,
            };
          })}
        />
      </Panel>

      {/* 3 — Opportunities summary */}
      <Panel eyebrowNum="03" label="Opportunities">
        <div className="grid grid-cols-2 gap-mx-2 sm:grid-cols-4">
          <Stat value={opps.openCount.toString()} caption="OPEN" />
          <Stat value={formatCurrency(opps.totalValue)} caption="PIPELINE VALUE" />
          <Stat value={formatCurrency(opps.weightedValue)} caption="WEIGHTED" />
          <Stat value={(opps.byStage.WON ?? 0).toString()} caption="WON" />
        </div>
        <div className="mt-mx-4 grid grid-cols-1 gap-mx-4 lg:grid-cols-2">
          <div>
            <p className="mono-label mb-mx-2 text-mx-grey-mid">BY STAGE</p>
            <Breakdown
              rows={OPPORTUNITY_STAGE.map((s) => ({
                key: s,
                label: s,
                count: opps.byStage[s] ?? 0,
              }))}
            />
          </div>
          <div>
            <p className="mono-label mb-mx-2 text-mx-grey-mid">BY CUSTOMER STATUS</p>
            <Breakdown
              rows={CUSTOMER_STATUS.map((s) => ({
                key: s,
                label: s.replace(/_/g, ' '),
                count: opps.byCustomerStatus[s] ?? 0,
              }))}
            />
          </div>
        </div>
      </Panel>

      {/* 4 — Opportunity current + next actions */}
      <Panel eyebrowNum="04" label="Opportunity actions">
        <Breakdown
          rows={OPPORTUNITY_ACTION.map((a) => {
            const next = NEXT_OPPORTUNITY_ACTION[a as OpportunityAction];
            const nextLabel = next ? ` → ${OPPORTUNITY_ACTION_LABELS[next]}` : '';
            return {
              key: a,
              label: `${OPPORTUNITY_ACTION_LABELS[a]}${nextLabel}`,
              count: opps.byCurrentAction[a] ?? 0,
              color: OPPORTUNITY_ACTION_COLORS[a].bg,
            };
          })}
        />
      </Panel>

      {/* 5 — Tasks (reserved) */}
      <Panel eyebrowNum="05" label="Tasks">
        <p className="text-mx-body-sm text-mx-grey-mid">
          A tasks summary lands here in a later design pass.
        </p>
      </Panel>
    </div>
  );
}
